import React, { useEffect, useState } from "react";
import { KeyRound, Beaker, Lock } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { Card, Button, Chip, Field, Input } from "@/ui";

const KIND_LABEL = {
  openrouter: "OpenRouter",
  anthropic: "Anthropic (Claude)",
  openai_compatible: "OpenAI-compatible",
};

// Shop owner (on the right plan): bring your own AI key. Validated with a real
// call on save; stored encrypted; only the last four are ever shown again.
export default function ShopAiKeys({ shopId, onToast }) {
  const [data, setData] = useState(null);
  const [keys, setKeys] = useState({});
  const [bases, setBases] = useState({});
  const [busy, setBusy] = useState("");
  const [test, setTest] = useState(null);
  const [errs, setErrs] = useState({});
  const [loadErr, setLoadErr] = useState("");

  const load = () => api.get(`/ai/shop${shopId ? `?shopId=${shopId}` : ""}`).then((r) => { setLoadErr(""); setData(r.data); }).catch((e) => setLoadErr(friendlyError(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [shopId]);

  const save = async (kind) => {
    setBusy("save-" + kind); setErrs((x) => ({ ...x, [kind]: "" }));
    try {
      const body = { shopId, kind, apiKey: (keys[kind] || "").trim(), baseUrl: bases[kind] || "" };
      const r = await api.post("/ai/shop/key", body);
      setData(r.data); setKeys((k) => ({ ...k, [kind]: "" }));
      onToast?.("Your key is saved and working.");
    } catch (e) { const m = friendlyError(e); setErrs((x) => ({ ...x, [kind]: m })); onToast?.(m); } finally { setBusy(""); }
  };

  const remove = async (kind) => {
    setBusy("rm-" + kind);
    try { const r = await api.post("/ai/shop/key/remove", { shopId, kind }); setData(r.data); onToast?.("Key removed."); }
    catch (e) { onToast?.(friendlyError(e)); } finally { setBusy(""); }
  };

  const runTest = async () => {
    setBusy("test");
    try { const r = await api.post("/ai/shop/test", { shopId }); setTest(r.data); }
    catch (e) { setTest({ ok: false, message: friendlyError(e) }); } finally { setBusy(""); }
  };

  if (loadErr) return (
    <Card data-testid="shop-ai-error">
      <p className="t-small" style={{ marginBottom: 12 }}>{loadErr}</p>
      <Button data-testid="shop-ai-retry" onClick={load}>Try again</Button>
    </Card>
  );
  if (!data) return <Card data-testid="shop-ai-loading"><div className="sk sk-title" style={{ marginBottom: 12 }} /><div className="sk sk-card" /></Card>;

  if (!data.allowed) {
    return (
      <Card data-testid="shop-ai-locked">
        <div className="row" style={{ gap: 10, marginBottom: 8 }}>
          <span className="ph-icon" style={{ margin: 0 }}><Lock size={18} strokeWidth={1.75} /></span>
          <h2 className="t-h2">your own AI key</h2>
        </div>
        <p className="muted t-small" style={{ maxWidth: 620 }}>
          Bringing your own AI key is a <strong>{data.byokPlan}</strong>-plan feature. On {data.byokPlan}, your
          AI usage bills to you, not to us. Ask your rep to upgrade to switch this on.
        </p>
      </Card>
    );
  }

  const ms = data.monthSpend || {};
  return (
    <Card data-testid="shop-ai-card">
      <div className="row-between" style={{ marginBottom: 8 }}>
        <div className="row" style={{ gap: 10 }}>
          <span className="ph-icon" style={{ margin: 0 }}><KeyRound size={18} strokeWidth={1.75} /></span>
          <h2 className="t-h2">your own AI key</h2>
        </div>
        <Chip tone="neutral" data-testid="shop-ai-spend">this month: {ms.charge || "\u20b10.00"}</Chip>
      </div>
      <p className="muted t-small" style={{ marginBottom: 12, maxWidth: 620 }}>
        Add your own key and your assistant runs on it — billed to you. We check it with a real call before saving.
        If it ever stops, we quietly keep your chats going on the shared key and tell you once a day.
      </p>
      {data.fellBackRecently ? (
        <div className="offline-banner" data-testid="shop-ai-fellback" style={{ marginBottom: 12 }}>
          Your key stopped working recently — we kept your chats going on the shared key. Please check it below.
        </div>
      ) : null}

      <div className="stack" style={{ gap: 12 }}>
        {(data.providers || []).map((p) => (
          <div key={p.kind} className="tbl-wrap" data-testid={`shop-ai-${p.kind}`} style={{ padding: 12 }}>
            <div className="row-between" style={{ marginBottom: 8 }}>
              <span className="t-small" style={{ fontWeight: 600 }}>{KIND_LABEL[p.kind] || p.kind}</span>
              {p.configured
                ? <Chip tone="good" dot data-testid={`shop-ai-last4-${p.kind}`}>ends …{p.last4}</Chip>
                : <Chip tone="warn" dot>not set</Chip>}
            </div>
            <div className="grid grid-2" style={{ gap: 10, alignItems: "end" }}>
              <Field label={p.configured ? "Replace key" : "API key"} htmlFor={`sk-${p.kind}`}>
                <Input id={`sk-${p.kind}`} type="password" data-testid={`shop-ai-key-${p.kind}`} value={keys[p.kind] || ""}
                  onChange={(e) => setKeys((k) => ({ ...k, [p.kind]: e.target.value }))} placeholder="paste your key" />
              </Field>
              {p.kind !== "anthropic" ? (
                <Field label="Base URL (optional)" htmlFor={`sb-${p.kind}`}>
                  <Input id={`sb-${p.kind}`} data-testid={`shop-ai-base-${p.kind}`} value={bases[p.kind] || ""}
                    onChange={(e) => setBases((b) => ({ ...b, [p.kind]: e.target.value }))} placeholder="https://…/v1" />
                </Field>
              ) : <div />}
            </div>
            <div className="row" style={{ gap: 8, marginTop: 10 }}>
              <Button size="sm" variant="primary" data-testid={`shop-ai-save-${p.kind}`} loading={busy === "save-" + p.kind}
                disabled={!(keys[p.kind] || "").trim()} onClick={() => save(p.kind)}>Save &amp; check</Button>
              {p.configured ? (
                <Button size="sm" variant="danger" data-testid={`shop-ai-remove-${p.kind}`} loading={busy === "rm-" + p.kind} onClick={() => remove(p.kind)}>Remove</Button>
              ) : null}
            </div>
            {errs[p.kind] ? <div className="err-text t-small" data-testid={`shop-ai-error-${p.kind}`} style={{ marginTop: 8 }}>{errs[p.kind]}</div> : null}
          </div>
        ))}
      </div>

      <div className="row" style={{ gap: 10, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
        <Button data-testid="shop-ai-test" loading={busy === "test"} onClick={runTest}><Beaker size={14} strokeWidth={1.75} />test my AI</Button>
        {test ? <span className="t-small" data-testid="shop-ai-test-result" style={{ color: test.ok ? "var(--good)" : "var(--bad)" }}>{test.message}</span> : null}
      </div>
    </Card>
  );
}
